// onboardingUi (Phase 49, plan 49-08): the PURE decision layer behind the onboarding surfaces.
//
// Every "one card at a time, never a third ask" rule lives here as a tested function instead of
// being spread across components (49-VALIDATION ONB-06 / ONB-07). Nothing here fetches, touches a
// browser global or renders copy: callers pass already-fetched state in and the Arabic copy lives
// in shared/src/notificationCopy.ts (ONBOARDING_COPY). `now` is injectable everywhere for tests.
//
// The user input is the shape AuthContext holds right after signup, i.e. the GET /auth/me payload
// plan 49-07 re-fetches (onboardingActive, signupSource, signupContext, createdAt as an ISO string
// or a Date). The reduced pre-refresh contextUser lacks those fields, so every selector returns
// null/false for it rather than guessing.
//
// Spec: 49-UI-SPEC.md S1 (welcome card), S2 (finish screen), S3 (thank-you second ask), DD-2,
// DD-3, DD-4, DD-7, DD-12; 49-CONTEXT.md D-07, D-08, D-09, D-12, D-13, D-14, D-23.

import {
  ONBOARDING_WINDOW_DAYS,
  RETURN_TOKEN_PARAM,
  RETURN_VARIANT_PARAM,
  REVIEW_TRIGGERS,
  WELCOME_CARD_TRIGGERS,
  type SignupContext,
  type UserOnboardingState,
} from "@shared/onboarding";
import type { PushSoftAskState } from "./pushSubscription";

const DAY_MS = 24 * 60 * 60 * 1000;

// ─── Input shapes (structural subsets, so tests and AuthContext's User both fit) ───

export type OnboardingUser = {
  createdAt?: Date | string;
  signupSource?: string;
  signupContext?: SignupContext | { trigger?: string };
  onboarding?: UserOnboardingState;
  onboardingActive?: boolean;
  full_name?: string;
  location?: string;
  avatar_url?: string;
};

// Subset of NotificationPreferences (api/notificationService.ts) the selectors read. Everything
// optional so a partially loaded or failed preferences query degrades to "hide item 3".
export type OnboardingPrefs = {
  prompts?: {
    whatsappPromptEligible?: boolean;
    whatsappOnboardingDeclined?: boolean;
    whatsappBellSuppressed?: boolean;
  };
  eligibility?: {
    whatsapp?: { state?: "consented" | "available" | "none" };
    push?: { subscribed?: boolean };
  };
};

// ─── Output shapes ───

export type WelcomeItem =
  | { key: "review"; done: boolean }
  | { key: "profile"; variant: "2a" | "2b" | "2c"; done: boolean }
  | { key: "notify"; variant: "whatsapp" | "push" | "done" };

export type WelcomeState = {
  trigger: string;
  items: WelcomeItem[];
  itemsTotal: number;
  itemsDone: number;
};

// ─── Helpers ───

const welcomeTriggers: readonly string[] = WELCOME_CARD_TRIGGERS;
const reviewTriggers: readonly string[] = REVIEW_TRIGGERS;

const filled = (v: string | undefined): boolean => typeof v === "string" && v.trim().length > 0;

const isItemDone = (item: WelcomeItem): boolean => (item.key === "notify" ? item.variant === "done" : item.done);

/** Account under ONBOARDING_WINDOW_DAYS old. Missing or unparseable createdAt counts as not young. */
export const isAccountYoung = ({
  createdAt,
  now = new Date(),
}: {
  createdAt: Date | string | undefined | null;
  now?: Date;
}): boolean => {
  if (!createdAt) return false;
  const at = new Date(createdAt).getTime();
  if (Number.isNaN(at)) return false;
  return now.getTime() - at < ONBOARDING_WINDOW_DAYS * DAY_MS;
};

// The gates every onboarding surface shares: the server says onboarding is on for this account
// (onboardingActive folds in AppSettings.onboarding.enabled), it is young, and it is not a
// WhatsApp Flow reviewer account.
const isOnboardingUser = (user: OnboardingUser | null | undefined, now: Date): user is OnboardingUser =>
  !!user &&
  user.onboardingActive === true &&
  user.signupSource !== "whatsapp_flow" &&
  isAccountYoung({ createdAt: user.createdAt, now });

// DD-2: the item copy adapts to what is missing. Profile completion mirrors the backend's
// maybeAwardProfileCompleted criteria (gamificationService: full_name, location, avatar_url).
const profileItem = (user: OnboardingUser): WelcomeItem => {
  const hasName = filled(user.full_name);
  const hasLocation = filled(user.location);
  const hasAvatar = filled(user.avatar_url);
  const missing = [hasName, hasLocation, hasAvatar].filter((x) => !x).length;
  if (missing === 0) return { key: "profile", variant: "2a", done: true };
  if (missing === 1 && !hasLocation) return { key: "profile", variant: "2b", done: false };
  if (missing === 1 && !hasAvatar) return { key: "profile", variant: "2c", done: false };
  return { key: "profile", variant: "2a", done: false };
};

// D-09 / DD-4: one ask per card. Already on (consent or a subscription) renders done; WhatsApp
// wins when the verified login phone is prompt-eligible; push only when the soft ask would show;
// otherwise hidden. A missing prefs payload (query error) hides it.
const notifyItem = ({
  prefs,
  pushState,
}: {
  prefs: OnboardingPrefs | undefined;
  pushState: PushSoftAskState;
}): WelcomeItem | null => {
  if (!prefs) return null;
  if (prefs.eligibility?.whatsapp?.state === "consented" || prefs.eligibility?.push?.subscribed === true) {
    return { key: "notify", variant: "done" };
  }
  if (prefs.prompts?.whatsappPromptEligible === true) return { key: "notify", variant: "whatsapp" };
  if (pushState === "ask") return { key: "notify", variant: "push" };
  return null;
};

// ─── Selectors ───

/**
 * S1 welcome card, or null. Visible only for a young, onboarding-active, non-WhatsApp-Flow account
 * whose signup trigger is a welcome-card (non-review) trigger, that has not dismissed the card and
 * still has at least one undone item (D-07). Unverified email signups still see it (DD-3).
 */
export const selectWelcomeCard = ({
  user,
  myReviewCount,
  prefs,
  pushState,
  now = new Date(),
}: {
  user: OnboardingUser | null | undefined;
  myReviewCount: number;
  prefs: OnboardingPrefs | undefined;
  pushState: PushSoftAskState;
  now?: Date;
}): WelcomeState | null => {
  if (!isOnboardingUser(user, now)) return null;
  const trigger = user.signupContext?.trigger;
  if (!trigger || !welcomeTriggers.includes(trigger)) return null;
  if (user.onboarding?.welcomeCardDismissedAt) return null;

  // D-08 order: review a store, complete profile, then notifications.
  const items: WelcomeItem[] = [{ key: "review", done: myReviewCount > 0 }, profileItem(user)];
  const notify = notifyItem({ prefs, pushState });
  if (notify) items.push(notify);

  const itemsDone = items.filter(isItemDone).length;
  if (itemsDone === items.length) return null;
  return { trigger, items, itemsTotal: items.length, itemsDone };
};

/**
 * S3 second WhatsApp ask on the review thank-you. Review-trigger signups only (non-review ones get
 * their single second ask as welcome card item 3, DD-7), young, still prompt-eligible, and the
 * finish-screen decline marker present. Accepting or dismissing clears eligibility, so it shows at
 * most once and there is never a third ask (D-14, D-23). Display gating only: consent still needs
 * an explicit click recorded server side.
 */
export const shouldShowThankYouWhatsappAsk = ({
  user,
  prefs,
  now = new Date(),
}: {
  user: OnboardingUser | null | undefined;
  prefs: OnboardingPrefs | undefined;
  now?: Date;
}): boolean => {
  if (!isOnboardingUser(user, now)) return false;
  const trigger = user.signupContext?.trigger;
  if (!trigger || !reviewTriggers.includes(trigger)) return false;
  return prefs?.prompts?.whatsappPromptEligible === true && prefs.prompts.whatsappOnboardingDeclined === true;
};

/** D-23: the bell/inbox WhatsappOptInCard hides while the server says the ask lives elsewhere. */
export const shouldSuppressBellWhatsappCard = ({ prefs }: { prefs: OnboardingPrefs | undefined }): boolean =>
  prefs?.prompts?.whatsappBellSuppressed === true;

/** DD-12: the signed return token (`ort`) and the message variant (`ov=b`) from a location.search. */
export const readReturnParams = (search: string): { token: string | null; variantB: boolean } => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const token = params.get(RETURN_TOKEN_PARAM);
  return { token: token ? token : null, variantB: params.get(RETURN_VARIANT_PARAM) === "b" };
};

// ─── S2 finish-account screen (consumed by PhoneAuth, plan 49-14) ───

/** D-12: the WhatsApp switch on the finish screen always starts off. */
export const FINISH_WHATSAPP_INITIAL = false;

/**
 * What the finish-screen submit does about WhatsApp. Already on wins: a login_otp consent is never
 * re-recorded or upgraded to source onboarding (D-13), and the switch is not rendered then.
 */
export const decideFinishSubmit = ({
  finishWhatsappOn,
  whatsappAlreadyOn,
}: {
  finishWhatsappOn: boolean;
  whatsappAlreadyOn: boolean;
}): "grant" | "decline" | "none" => {
  if (whatsappAlreadyOn) return "none";
  return finishWhatsappOn ? "grant" : "decline";
};

/** The consent request: option by reference (the server resolves the number), never a phone. */
export const buildFinishConsentRequest = () => ({
  option: { kind: "login" } as const,
  source: "onboarding" as const,
});
